/**
 * Kanban Page
 *
 * Pagina principale della Kanban board dinamica (Planner-style). Possiede la query della
 * board (filtri dall'URL via useBoardFilters) e la passa come prop sia a BoardFiltersBar
 * (per i badge metadata) sia a DynamicKanbanBoard, evitando una doppia richiesta identica.
 */
import { useState } from 'react';
import { Button, Container, Group, Stack, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import BoardFiltersBar from '../../components/kanban/BoardFiltersBar';
import DynamicKanbanBoard from '../../components/kanban/DynamicKanbanBoard';
import TaskCreateModal from '../../components/modals/TaskCreateModal';
import TaskDetailModal from '../../components/modals/TaskDetailModal';
import { useBoardFilters } from '../../hooks/use-board-filters';
import { useKanbanPreferences } from '../../hooks/use-kanban-preferences';
import { useDynamicKanbanBoard } from '../../hooks/use-kanban-columns';

export default function KanbanPage() {
  const { filters, updateFilters, resetFilters, myUserId, isOnlyMine, toggleOnlyMine } = useBoardFilters();
  const prefs = useKanbanPreferences(myUserId);
  const { data: board, isLoading, isError } = useDynamicKanbanBoard(filters);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createDefaultColumnId, setCreateDefaultColumnId] = useState<number | undefined>(undefined);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleAddTask = (columnId?: number) => {
    setCreateDefaultColumnId(columnId);
    setIsCreateModalOpen(true);
  };

  const handleTaskClick = (taskId: number) => {
    setEditingTaskId(taskId);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setEditingTaskId(null);
  };

  return (
    <Container size="xl" fluid py="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Kanban Board</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => handleAddTask(undefined)}>
            Nuovo Task
          </Button>
        </Group>

        <BoardFiltersBar
          filters={filters}
          onFiltersChange={updateFilters}
          onReset={resetFilters}
          isOnlyMine={isOnlyMine}
          onToggleOnlyMine={toggleOnlyMine}
          showCovers={prefs.showCovers}
          onShowCoversChange={prefs.setShowCovers}
          showCompleted={prefs.showCompleted}
          onShowCompletedChange={prefs.setShowCompleted}
          metadata={board?.metadata}
        />

        <DynamicKanbanBoard
          board={board}
          isLoading={isLoading}
          isError={isError}
          showCovers={prefs.showCovers}
          isCompletedExpanded={prefs.isCompletedExpanded}
          onToggleCompleted={prefs.toggleColumnCompleted}
          onTaskClick={handleTaskClick}
          onAddTask={handleAddTask}
          height="calc(100vh - 260px)"
          showAlerts
        />
      </Stack>

      <TaskCreateModal
        opened={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={() => setIsCreateModalOpen(false)}
        defaultRequestId={filters.requestId}
        defaultColumnId={createDefaultColumnId}
      />

      {editingTaskId != null && (
        // Nessun onTaskUpdated: il tab Dettagli resta sempre editabile (niente più modalità
        // Modifica/Annulla), quindi salvare non deve chiudere il modal — l'utente può
        // continuare a lavorare su Checklist/Allegati. La cache è già invalidata dagli hook.
        <TaskDetailModal taskId={editingTaskId} opened={isDetailModalOpen} onClose={closeDetailModal} />
      )}
    </Container>
  );
}
